import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Fab, Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ServiceRequestModal from 'components/common/ServiceRequestModal';
import LogoIcon from '/Logo.svg';
import { typography } from 'theme';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubmitSupportRequestMutation } from 'store/api/support.apislice';
import toast from 'react-hot-toast';
import { isNative } from 'utils/platform.utils';

// Component scope - Constants
const AUTO_CLOSE_DELAY = 2000; // 2 seconds

//Support Reasons 
const Support_Reasons = [
  "KYC QUERIES",
  "UNABLE TO PLACE ORDRES",
  "CLIENT REGISTRATION",
  "XSIP /STP/SWP REGISTRATION",
  "PASSWORD RESET",
  "UCC ACTIVATION",
  "AMC/SCHEME ISSUES",
  "MANDATE",
  "ENTRY UPDATION",
  "REFUND",
  "ALLOTEMENT TRACKING"
];

// Component scope - Style functions that use theme
const getFABContainerSX = (theme) => ({
  zIndex: 9999,
  position: isNative() ? 'sticky' : 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(0),
  marginRight: theme.spacing(6),
  display: 'flex',
  justifyContent: 'flex-end',
  [theme.breakpoints.down('md')]: {
    bottom: theme.spacing(8),
    marginRight: theme.spacing(2),
  }
});

const getFABSX = (theme) => {
  // Use white color for FAB background (works in both light and dark modes)
  const bgColor = '#ffffff';

  return {
    bgcolor: bgColor,
    color: theme.palette.warning.contrastText,
    boxShadow: theme.shadows[3],
    '&:hover': {
      bgcolor: alpha(bgColor, 0.9),
      boxShadow: theme.shadows[6]
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: { xs: 60, md: 80 },
    height: { xs: 60, md: 80 },
    borderRadius: theme.spacing(1),
    textTransform: 'none',
  };
};

const getLogoSX = (theme) => ({
  width: { xs: 24, md: 32 },
  height: { xs: 24, md: 32 },
  mb: theme.spacing(0.5),
  objectFit: 'contain'
});

const getTextSX = (theme) => ({
  fontSize: {
    xs: '0.625rem', // 10px for mobile
    md: typography.fontSize.helperText
  },
  fontWeight: typography.fontWeight.semibold,
  textAlign: 'center',
  color: '#000000',
});

/**
 * Count words in a text string
 * @param {string} text - Text to count words in
 * @returns {number} Word count
 */
const countWords = (text) => {
  if (!text) return 0;
  const trimmed = text.trim();
  if (trimmed === '') return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
};

/**
 * Service Request Container
 * Handles FAB button rendering, modal state, and form logic for service requests
 * @component
 */
const ServiceRequestContainer = ({ disabled = false, contactrmModal = false, modalTitle = 'Need Help? Reach Out to us', onModalClose }) => {
  // States, refs, selectors, RTK Query hooks
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false || contactrmModal);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [query, setQuery] = useState('');
  const [submitSupportRequest, { isLoading: isSubmittingRequest }] = useSubmitSupportRequestMutation();

  //use for the add the reason dropdown in the service request modal and also for the category in the support request submission
  const [reason, setReason] = useState('');

  // Calculate word count and validation
  const wordCount = countWords(query);
  const isValid = query?.trim().length > 0 && query?.length <= 300 && reason !== '';

  const pathname = location.pathname;
  let currentPath = '';
  if (pathname.includes('investments')) {
    currentPath = 'INVESTMENTS';
  } else if (pathname.includes('portfolio')) {
    currentPath = 'PORTFOLIO';
  } else if (pathname.includes('profile')) {
    currentPath = 'PROFILE';
  } else if (pathname.includes('sip')) {
    currentPath = 'SIP';
  } else if (pathname.includes('mutual-funds')) {
    currentPath = 'MUTUAL FUNDS';
  }


  console.log('currentPath', currentPath);


  // Other functions and logic
  const handleOpen = () => {
    setModalOpen(true);
  };

  const handleClose = () => {
    setShowSuccess(false);
    setIsSubmitting(false);
    setModalOpen(false);
    setQuery('');
    setReason('');

    if (onModalClose) {
      onModalClose();
    }

    if (contactrmModal && !onModalClose) {
      navigate('/explore');
    }
  };

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate before submitting
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the support API
      const response = await submitSupportRequest({
        request: query,
        reason: reason,
        category: currentPath || 'GENERAL'
      }).unwrap();
      // Show success message
      setShowSuccess(true);

      // Auto close after delay
      setTimeout(() => {
        handleClose();
      }, AUTO_CLOSE_DELAY);
    } catch (error) {
      console.error('Failed to submit service request:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to submit support request';
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  // Return/rendering
  return (
    <>
      {!contactrmModal && (
        <Box sx={getFABContainerSX(theme)}>
          <Fab
            aria-label="Need help"
            onClick={handleOpen}
            disabled={disabled}
            sx={getFABSX(theme)}
          >
            <Box
              component="img"
              src={LogoIcon}
              alt="Help icon"
              sx={getLogoSX(theme)}
            />
            <Box sx={getTextSX(theme)}>Need Help?</Box>
          </Fab>
        </Box>
      )}

      <ServiceRequestModal
        open={modalOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        loading={isSubmitting || isSubmittingRequest}
        showSuccess={showSuccess}
        query={query}
        onQueryChange={handleQueryChange}
        reason={reason}
        reasons={Support_Reasons}
        onReasonChange={(e) => setReason(e.target.value)}
        disabled={!isValid || isSubmitting || isSubmittingRequest}
        minLength={10}
        maxLength={300}
        modalTitle={modalTitle}
      />
    </>
  );
};

ServiceRequestContainer.propTypes = {
  disabled: PropTypes.bool,
  contactrmModal: PropTypes.bool,
  modalTitle: PropTypes.string,
  onModalClose: PropTypes.func
};

export default ServiceRequestContainer;
